import {
  LogoutOutlined,
  SettingOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { history, useModel } from '@umijs/max';
import type { MenuProps } from 'antd';
import { Spin } from 'antd';
import { createStyles } from 'antd-style';
import React from 'react';
import { flushSync } from 'react-dom';
import { authService } from '@/services/auth/authService';
import HeaderDropdown from '../HeaderDropdown';

export type GlobalHeaderRightProps = {
  menu?: boolean;
  children?: React.ReactNode;
};

export const AvatarName = () => {
  const { initialState } = useModel('@@initialState');
  const { currentUser } = initialState || {};
  return <span className="anticon">{currentUser?.name}</span>;
};

const useStyles = createStyles(({ token }) => {
  return {
    action: {
      display: 'flex',
      height: '48px',
      marginLeft: 'auto',
      overflow: 'hidden',
      alignItems: 'center',
      padding: '0 8px',
      cursor: 'pointer',
      borderRadius: token.borderRadius,
      '&:hover': {
        backgroundColor: token.colorBgTextHover,
      },
    },
  };
});

export const AvatarDropdown: React.FC<GlobalHeaderRightProps> = ({
  menu,
  children,
}) => {
  /**
   * Logout using Keycloak service
   * This will:
   * 1. Clear React state immediately
   * 2. End SSO session on Keycloak server
   * 3. Clear all local auth state
   * 4. Redirect to login page
   */
  const loginOut = async () => {
    try {
      if (process.env.NODE_ENV === 'development') {
        console.log(
          '🚪 AvatarDropdown: Logout button clicked - clearing all data',
        );
      }

      // ✅ Clear React state IMMEDIATELY (before Keycloak redirect)
      // This ensures UI updates immediately
      if (setInitialState) {
        setInitialState((s) => ({
          ...s,
          currentUser: undefined,
          menu: undefined,
        }));
      }

      // Use the proper Keycloak logout flow
      await authService.logout();

      // Note: authService.logout() will redirect, so code after won't execute
    } catch (error) {
      console.error('❌ AvatarDropdown: Logout failed:', error);
      // Fallback: clear state and redirect manually
      authService.clearAuthData();

      if (setInitialState) {
        setInitialState((s) => ({
          ...s,
          currentUser: undefined,
          menu: undefined,
        }));
      }

      window.location.href = '/user/login';
    }
  };
  const { styles } = useStyles();

  const { initialState, setInitialState } = useModel('@@initialState');

  const onMenuClick: MenuProps['onClick'] = (event) => {
    const { key } = event;
    if (key === 'logout') {
      flushSync(() => {
        setInitialState((s) => ({ ...s, currentUser: undefined }));
      });
      loginOut();
      return;
    }
    history.push(`/account/${key}`);
  };

  const loading = (
    <span className={styles.action}>
      <Spin
        size="small"
        style={{
          marginLeft: 8,
          marginRight: 8,
        }}
      />
    </span>
  );

  if (!initialState) {
    return loading;
  }

  const { currentUser } = initialState;

  if (!currentUser || !currentUser.name) {
    return loading;
  }

  const menuItems = [
    ...(menu
      ? [
          {
            key: 'center',
            icon: <UserOutlined />,
            label: 'Personal Center',
          },
          {
            key: 'settings',
            icon: <SettingOutlined />,
            label: 'Personal Settings',
          },
          {
            type: 'divider' as const,
          },
        ]
      : []),
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Logout',
    },
  ];

  return (
    <HeaderDropdown
      menu={{
        selectedKeys: [],
        onClick: onMenuClick,
        items: menuItems,
      }}
    >
      {children}
    </HeaderDropdown>
  );
};
